import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";
import { PW } from "../internal-ui/pewter-shell";
import {
  SEAT_HOLD_HOURS,
  WOMEN_INK,
  WOMEN_PINK,
  WOMEN_WASH,
  type TripMoneyRows,
} from "../lib/women-trip";

/**
 * The women-only pieces of the booking surfaces (owner-approved lab,
 * women-only-lab 2026-10-27): the seats bar, the three money rows, the
 * deposit note and the three steps. The pink accent lives here and nowhere
 * else, so a women-only trip is the only page that wears it.
 */

/** "6 seats left" over a thin bar of the seats already taken. */
export function TripSeatsBar({
  seatLimit,
  seatsLeft,
  className = "",
}: {
  seatLimit: number | null;
  seatsLeft: number | null;
  className?: string;
}) {
  const { t } = useTranslation();
  if (seatsLeft == null) return null;
  const limit = seatLimit && seatLimit > 0 ? seatLimit : null;
  const taken = limit ? Math.min(100, Math.max(0, Math.round(((limit - seatsLeft) / limit) * 100))) : null;
  const low = seatsLeft > 0 && seatsLeft <= 3;
  return (
    <div className={className} data-testid="trip-seats">
      <div className="mb-1 flex items-center justify-between gap-2 font-sans text-[12px] font-semibold">
        <span style={{ color: PW.muted }}>{limit ? t("women.seats_total", { count: limit }) : null}</span>
        <span style={{ color: seatsLeft === 0 ? PW.muted : low ? WOMEN_PINK : WOMEN_INK }}>
          {seatsLeft === 0 ? t("women.full") : t("women.seats_left", { count: seatsLeft })}
        </span>
      </div>
      {taken != null && (
        <div
          className="h-[5px] overflow-hidden rounded-full"
          style={{ background: "rgba(17,14,57,0.08)" }}
          role="progressbar"
          aria-label={t("women.seats_taken_label")}
          aria-valuemin={0}
          aria-valuemax={limit ?? 0}
          aria-valuenow={limit ? limit - seatsLeft : 0}
        >
          <div className="h-full rounded-full" style={{ width: `${taken}%`, background: WOMEN_PINK }} />
        </div>
      )}
    </div>
  );
}

function MoneyRow({
  label,
  value,
  currency,
  strong = false,
  testId,
}: {
  label: string;
  value: string;
  currency: string;
  strong?: boolean;
  testId?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 font-sans text-[13px]" data-testid={testId}>
      <span className="min-w-0 font-medium" style={{ color: strong ? PW.navy : PW.muted }}>
        {label}
      </span>
      <span className="whitespace-nowrap font-semibold tabular-nums" style={{ color: PW.navy }}>
        {value} <span className="text-[11px]">{currency}</span>
      </span>
    </div>
  );
}

/**
 * Your seat, the deposit to hold it, the rest on the day. `rows` comes from
 * `tripMoneyRows`, already in the display currency, so the last two always
 * add up to the first.
 */
export function TripMoneyRowsView({
  rows,
  seats,
  format,
  className = "",
  depositLabel,
}: {
  rows: TripMoneyRows;
  seats: number;
  /** Formats an amount ALREADY in the display currency. */
  format: (amount: number) => { value: string; currency: string };
  className?: string;
  /** "Deposit paid" once the organiser has it. */
  depositLabel?: string;
}) {
  const { t } = useTranslation();
  const seat = format(rows.seat);
  const deposit = format(rows.deposit);
  const rest = format(rows.rest);
  return (
    <div className={className} data-testid="trip-money">
      <MoneyRow
        label={t("women.money_seat", { count: Math.max(1, seats) })}
        value={seat.value}
        currency={seat.currency}
        strong
        testId="trip-money-seat"
      />
      <MoneyRow
        label={depositLabel ?? t("women.money_deposit")}
        value={deposit.value}
        currency={deposit.currency}
        testId="trip-money-deposit"
      />
      <MoneyRow label={t("women.money_rest")} value={rest.value} currency={rest.currency} testId="trip-money-rest" />
    </div>
  );
}

/** The pink note: who gets the deposit and how long the seat is held. */
export function TripNote({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p
      className="rounded-[12px] px-3 py-2 font-sans text-[12.5px] font-medium leading-snug"
      style={{ background: WOMEN_WASH, color: WOMEN_INK }}
      data-testid={testId}
    >
      {children}
    </p>
  );
}

/** The deposit note's sentence, with or without an organiser's name. */
export function useDepositNote(organiserName: string | null | undefined): string {
  const { t } = useTranslation();
  return organiserName
    ? t("women.deposit_note", { organiser: organiserName, hours: SEAT_HOLD_HOURS })
    : t("women.deposit_note_generic", { hours: SEAT_HOLD_HOURS });
}

/** Reserved, Deposit received, Confirmed: three bars, filled up to `step`. */
export function TripStepsBar({ step, className = "" }: { step: 0 | 1 | 2; className?: string }) {
  const { t } = useTranslation();
  const labels = [t("women.step_reserved"), t("women.step_deposit"), t("women.step_confirmed")];
  return (
    <ol className={`flex items-start gap-1.5 ${className}`} aria-label={t("women.steps_label")} data-testid="trip-steps">
      {labels.map((label, i) => {
        const on = i <= step;
        return (
          <li key={label} className="min-w-0 flex-1" aria-current={i === step ? "step" : undefined}>
            <div className="h-[4px] rounded-full" style={{ background: on ? WOMEN_PINK : "rgba(17,14,57,0.1)" }} />
            <div
              className="mt-1 font-sans text-[11px] font-semibold leading-tight"
              style={{ color: on ? WOMEN_INK : PW.muted }}
            >
              {label}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** "Women-only trip · Organised by She Escapes", under the trip's name. */
export function WomenTripLine({
  organiserName,
  organiserHref,
  className = "",
}: {
  organiserName: string | null;
  organiserHref: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <p
      className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 font-sans text-[13.5px] font-semibold ${className}`}
      data-testid="women-trip-line"
    >
      <span className="inline-flex items-center gap-1" style={{ color: WOMEN_INK }}>
        <Icon name="ri-user-heart-line" className="text-[15px]" style={{ color: WOMEN_PINK }} aria-hidden />
        {t("women.women_only_trip")}
      </span>
      {organiserName ? (
        <>
          <span aria-hidden style={{ color: PW.muted }}>
            ·
          </span>
          {organiserHref ? (
            <a
              href={organiserHref}
              className="inline-flex min-h-6 items-center underline decoration-[rgba(114,36,62,0.35)] underline-offset-2"
              style={{ color: WOMEN_INK }}
            >
              {t("women.organised_by", { name: organiserName })}
            </a>
          ) : (
            <span style={{ color: PW.muted }}>{t("women.organised_by", { name: organiserName })}</span>
          )}
        </>
      ) : null}
    </p>
  );
}

/** A departure chip at rest: white, one navy hairline. Never the band. */
const DEPARTURE_REST = {
  background: BEVEL_WHITE_FACE,
  color: PW.navy,
  boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}`,
};
/** Chosen: the navy face, no drop shadow (owner, 2026-10-11). */
const DEPARTURE_ON = { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON };

/**
 * "Where do you leave from?": one chip per departure city. Shown only when
 * the trip leaves from several (or names its one) city; the sheet will not
 * move on until one is picked, and the pick is sent as `tripDeparture`.
 */
export function TripDeparturePicker({
  departures,
  value,
  onChange,
  showError = false,
  className = "",
  compact = false,
}: {
  departures: string[];
  value: string | null;
  onChange: (city: string) => void;
  /** Set once Continue was pressed without a pick. */
  showError?: boolean;
  className?: string;
  /** The sidebar's smaller label. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (departures.length === 0) return null;
  const missing = showError && !(value && departures.includes(value));
  return (
    <div className={className} data-testid="trip-departure">
      <p
        id="trip-departure-label"
        className={
          compact
            ? "mb-2 font-sans text-[12px] font-semibold"
            : "mb-2 font-sans text-[11px] font-bold uppercase tracking-[0.1em]"
        }
        style={{ color: compact ? PW.navy : PW.muted }}
      >
        {t("women.departure_question")}
      </p>
      <div role="radiogroup" aria-labelledby="trip-departure-label" className="flex flex-wrap gap-2">
        {departures.map((city) => {
          const on = value === city;
          return (
            <button
              key={city}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(city)}
              className="mj-press inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-4 py-1.5 font-sans text-[13px] font-semibold"
              style={on ? DEPARTURE_ON : DEPARTURE_REST}
              data-testid="trip-departure-chip"
            >
              <Icon name="ri-map-pin-line" className="text-[14px]" aria-hidden />
              {city}
            </button>
          );
        })}
      </div>
      {missing && (
        <p role="alert" className="mt-2 font-sans text-[12px] font-semibold text-[#B42318]" data-testid="trip-departure-missing">
          {t("women.departure_required")}
        </p>
      )}
    </div>
  );
}
